/**
 * Plain text of the copyable cards (ad texts, photo briefs), for the copy button: the owner
 * pastes it to the design team or into Google Ads. Character counts follow Google's rule
 * (Unicode code points), so the numbers shown match what Google checks.
 */
import type { AdCopyCard, BriefCard } from '../../../supabase/functions/_shared/assistant/cards.ts';
import { charCount, IMAGE_SPECS } from '../../../supabase/functions/_shared/core/adAssets.ts';
import type { Language } from './i18n.ts';

const LABELS = {
  ar: {
    headlines: 'العناوين',
    longHeadlines: 'العناوين الطويلة',
    descriptions: 'الأوصاف',
    goal: 'الهدف',
    branch: 'الفرع',
    landscape: 'عرضي',
    square: 'مربع',
    portrait: 'طولي',
    size: 'المقاس',
  },
  en: {
    headlines: 'Headlines',
    longHeadlines: 'Long headlines',
    descriptions: 'Descriptions',
    goal: 'Goal',
    branch: 'Branch',
    landscape: 'Landscape',
    square: 'Square',
    portrait: 'Portrait',
    size: 'Size',
  },
} as const;

export function adCopyText(card: AdCopyCard, language: Language): string {
  const t = LABELS[language];
  const section = (title: string, lines: readonly string[]) =>
    lines.length === 0 ? [] : [title, ...lines.map((l) => `- ${l} (${String(charCount(l))})`), ''];
  return [
    ...section(t.headlines, card.headlines),
    ...section(t.longHeadlines, card.longHeadlines),
    ...section(t.descriptions, card.descriptions),
  ]
    .join('\n')
    .trim();
}

export function orientationLabel(
  orientation: keyof typeof IMAGE_SPECS,
  language: Language,
): string {
  const spec = IMAGE_SPECS[orientation];
  return `${LABELS[language][orientation]} ${spec.ratio} — ${spec.recommended}`;
}

export function briefText(card: BriefCard, language: Language): string {
  const t = LABELS[language];
  return [
    card.title,
    `${t.goal}: ${card.goal}`,
    ...(card.branchCode ? [`${t.branch}: ${card.branchCode}`] : []),
    '',
    ...card.shots.map(
      (s, i) =>
        `${String(i + 1)}. ${s.subject}\n   ${t.size}: ${orientationLabel(s.orientation, language)}\n   ${s.notes}`,
    ),
  ].join('\n');
}
